import type { ChangeEvent } from "react";
import {
  DlfMatcher,
  DlfMatcherProperties,
} from "../../models/matchers/DlfMatcher";
import { MatcherFactory } from "../../models/matchers/factory/MatcherFactory";
import {
  FuzzyFieldMatcher,
  FuzzyFieldMatcherData,
} from "../../models/matchers/FuzzyFieldMatcher";
import {
  KeyValueMatcher,
  KeyValueMatcherData,
} from "../../models/matchers/KeyValueMatcher";
import {
  RegexFieldMatcher,
  RegexFieldMatcherData,
} from "../../models/matchers/RegexFieldMatcher";
import {
  DlfMatcherTypes,
  getDlfMatcherTypes,
  getDlfMatcherTypesDisplayValue,
  isDlfMatcherTypes,
} from "../../types/rules/DlfMatcherTypes";
import { ExpandingTooltipReturn } from "../hooks/useAdvancedTooltip";

export { KvExtendedDlfTypeChanger, changeMatcherType };

type ExtendedTypes =
  | "F_KV_REGEX"
  | "F_KV_FUZZY"
  | Omit<DlfMatcherTypes, "F_KEYVALUE">;
type Props = {
  matcher: DlfMatcher;
  defaultMatcherThreshold: number;
  numberOfSuggestions: number;
  changedType: (newMatcher: DlfMatcher) => void;
  tProps?: ExpandingTooltipReturn["triggerProps"];
};
const baseOptions: {
  key: ExtendedTypes;
  value: string;
}[] = ["F_KV_REGEX", "F_KV_FUZZY"]
  .concat(
    getDlfMatcherTypes().filter((x) => ["F_REGEX", "F_DEFAULT"].includes(x)),
  )
  .map((x) => {
    let displayValue = "";
    if (isDlfMatcherTypes(x)) {
      displayValue = getDlfMatcherTypesDisplayValue(x);
    } else if (x === "F_KV_REGEX") {
      displayValue = "Key Value Regex";
    } else if (x === "F_KV_FUZZY") {
      displayValue = "Key Value Fuzzy";
    }
    return {
      key: x as ExtendedTypes,
      value: displayValue,
    };
  });

const changeMatcherType = (
  dlfMatcher: DlfMatcher,
  type: ExtendedTypes,
  defaultMatcherThreshold: number,
  numberOfValidations: number,
): DlfMatcher => {
  let newMatcher;
  /* determine if only keyValueMatcher.keyMatcher.matcher should be changed  */
  if (
    (type === "F_KV_FUZZY" || type === "F_KV_REGEX") &&
    dlfMatcher.getDiscriminator() === "F_KEYVALUE"
  ) {
    console.debug("Changing to KeyValueMatcher key matcher to" + type);
    const newMatcherData = {
      ...dlfMatcher.toMatcherData(),
    } as KeyValueMatcherData;
    newMatcherData.threshold = defaultMatcherThreshold;

    const newKeyMatcherData: Partial<DlfMatcherProperties> = {
      fieldName: dlfMatcher.fieldName,
      reusable: true,
      // update threshold when changing between types
      threshold: defaultMatcherThreshold,
    };

    // get fuzzyKey or regex from keyValueMatcher.keyMatcher
    const currMatcherKey =
      newMatcherData.keyMatcher.matcher.discriminator === "F_REGEX"
        ? (newMatcherData.keyMatcher.matcher as RegexFieldMatcher).regex
        : (newMatcherData.keyMatcher.matcher as FuzzyFieldMatcher).fuzzyKey;

    // set old key onto new KeyMatcher regardless of type
    if (type === "F_KV_REGEX") {
      newKeyMatcherData.discriminator = "F_REGEX";
      (newKeyMatcherData as RegexFieldMatcherData).regex = currMatcherKey;
    } else if (type === "F_KV_FUZZY") {
      newKeyMatcherData.discriminator = "F_FUZZY";
      (newKeyMatcherData as FuzzyFieldMatcherData).fuzzyKey = currMatcherKey;
    }
    newMatcherData.keyMatcher.matcher =
      newKeyMatcherData as DlfMatcherProperties;

    newMatcher = MatcherFactory.createDlfMatcher(newMatcherData);
  } else if (type === "F_KV_FUZZY" || type === "F_KV_REGEX") {
    /* Change to a KeyValueMatcher from any other matcher, set KeyMatcher to specific type. */
    const keyMatcherType = type === "F_KV_FUZZY" ? "F_FUZZY" : "F_REGEX";
    const newKeyMatcher = MatcherFactory.changeDlfMatcher(
      dlfMatcher.toMatcherData(),
      keyMatcherType,
      defaultMatcherThreshold,
    );
    newMatcher = MatcherFactory.changeDlfMatcher(
      dlfMatcher.toMatcherData(),
      "F_KEYVALUE",
      defaultMatcherThreshold,
    ) as KeyValueMatcher;
    newMatcher.keyMatcher.matcher = newKeyMatcher;
  } else {
    const newType: DlfMatcherTypes = ["F_KV_FUZZY", "F_KV_REGEX"].includes(
      type as string,
    )
      ? "F_KEYVALUE"
      : (type as DlfMatcherTypes);
    newMatcher = MatcherFactory.changeDlfMatcher(
      dlfMatcher.toMatcherData(),
      newType,
      defaultMatcherThreshold,
    );
  }

  // Change KV ValueMatcher to .+ or $Valid
  if (newMatcher.discriminator === "F_KEYVALUE") {
    const kvMatcher = newMatcher as KeyValueMatcher;
    if (kvMatcher.valueMatcher.matcher.discriminator === "F_REGEX") {
      (kvMatcher.valueMatcher.matcher as RegexFieldMatcher).regex =
        numberOfValidations > 0 ? "$Valid" : ".+";
    }
  }

  return newMatcher;
};

/**
 * <DlfMatcherTypes /> but with KeyValue specific entries.
 * @param props
 * @returns
 */
const KvExtendedDlfTypeChanger = (props: Props) => {
  const options: typeof baseOptions = baseOptions;

  let matcherType = props.matcher.getDiscriminator() as
    | ExtendedTypes
    | DlfMatcherTypes;
  if (matcherType === "F_KEYVALUE") {
    if (
      (
        props.matcher as KeyValueMatcher
      ).keyMatcher.matcher.getDiscriminator() === "F_FUZZY"
    ) {
      matcherType = "F_KV_FUZZY";
    } else {
      matcherType = "F_KV_REGEX";
    }
  }

  return (
    <select
      className={"dlfTypeChanger"}
      value={matcherType as string}
      onChange={(e: ChangeEvent<HTMLSelectElement>) =>
        props.changedType(
          changeMatcherType(
            props.matcher,
            e.target.value as ExtendedTypes,
            props.defaultMatcherThreshold,
            props.numberOfSuggestions,
          ),
        )
      }
      {...props.tProps}
    >
      {options.map((opt) => (
        <option
          key={opt.key as string}
          value={opt.key as string}
          label={opt.value}
        >
          {opt.value}
        </option>
      ))}
    </select>
  );
};
